import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  useGetDocsByQuery,
  useFetchDocsByIds,
} from "../../../components/Firebase/useFirebaseQueries";
import { getTodayStartEndTimestamps } from "../../../utils/Utils";
import useLocalStorage from "../../../Hooks/UseLocalstorage";
import Loader from "../../../components/Loader/Loader";

export default function ProductMarginReport() {
  const queryClient = useQueryClient();
  const [pharmacyId] = useLocalStorage("pharmacyId");
  const { startTimestamp, endTimestamp } = getTodayStartEndTimestamps();
  const [fromDate, setFromDate] = useState(startTimestamp);
  const [toDate, setToDate] = useState(endTimestamp);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [docIds, setDocIds] = useState([]);
  const conditions = useMemo(
    () => [
      { field: "pharmacyId", operator: "==", value: pharmacyId },
      { field: "medicineId", operator: "in", value: docIds },
    ],
    [pharmacyId, docIds]
  );
  const { data: medicineInventory } = useFetchDocsByIds(
    "medicineInventory",
    conditions,
    shouldFetch
  );
  const { data: bills = [], isLoading: billLoading } = useGetDocsByQuery(
    "pharmacyBills",
    [
      { field: "pharmacyId", operator: "==", value: pharmacyId },
      { field: "billDate", operator: ">=", value: fromDate },
      { field: "billDate", operator: "<=", value: toDate },
    ],
    true
  );
  const [billDetails, setBillDetails] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const rowsPerPage = 10;

  useEffect(() => {
    if (bills.length) {
      const aggregateBills = () => {
        const billMap = new Map();
        bills.forEach((bill) => {
          const key = `${bill.billNumber}_${bill.billDate}`;
          if (!billMap.has(key)) {
            billMap.set(key, {
              billNumber: bill.billNumber,
              billDate: bill.billDate,
              medicineDetails: [],
            });
          }
          billMap.get(key).medicineDetails.push(...(bill.medicineDetails || []));
        });

        const aggregated = Array.from(billMap.values()).map((bill) => ({
          billNumber: bill.billNumber,
          billDate: bill.billDate,
          medicineDetails: bill.medicineDetails,
        }));

        setBillDetails(aggregated);
        const ids = aggregated.flatMap((bill) =>
          bill.medicineDetails.map((med) => med.medicineId)
        );
        if (ids?.length > 0) {
          getMatchedMedicinesFromInventory(ids);
        }
      };

      aggregateBills();
    }
  }, [bills]);

  useEffect(() => {
    if (medicineInventory?.length > 0) {
      const mergedBills = billDetails.map((bill) => {
        const enrichedMedicines = bill.medicineDetails.map((med) => {
          const matchFound = medicineInventory.find(
            (item) => item.medicineId === med.medicineId
          );
          return matchFound ? { ...matchFound, ...med, quantity: med.quantity } : med;
        });
        return { ...bill, medicineDetails: enrichedMedicines };
      });
      setFilteredBills(mergedBills);
    }
  }, [medicineInventory, billDetails]);

  const getMatchedMedicinesFromInventory = (ids) => {
    setDocIds(ids);
    setShouldFetch(true);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const selectedFromDate = new Date(value).getTime();
      if (selectedFromDate > toDate) {
        alert("From date cannot be greater than To date.");
        return;
      }
      setFromDate(selectedFromDate);
    } else {
      setFromDate(startTimestamp);
    }
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const selectedToDate = new Date(value).getTime();
      if (selectedToDate < fromDate) {
        alert("To date cannot be earlier than From date.");
        return;
      }
      setToDate(selectedToDate);
    } else {
      setToDate(endTimestamp);
    }
    setCurrentPage(1);
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Product Margin Report", 105, 10, { align: "center" });
    doc.setFontSize(10);
    doc.text(
      `From: ${format(new Date(fromDate), "dd/MM/yyyy")} To: ${format(
        new Date(toDate),
        "dd/MM/yyyy"
      )}`,
      105,
      15,
      { align: "center" }
    );

    const tableData = filtered.map((bill, index) => [
      index + 1,
      bill.billNumber || "",
      format(new Date(bill.billDate), "dd/MM/yyyy"),
      Number.isFinite(bill.totalSaleValue) ? bill.totalSaleValue.toFixed(2) : "0.00",
      Number.isFinite(bill.totalCostValue) ? bill.totalCostValue.toFixed(2) : "0.00",
      Number.isFinite(bill.grossMarginPercent) ? bill.grossMarginPercent.toFixed(2) : "0.00",
      bill.totalQuantity || 0,
      Number.isFinite(bill.totalMargin) ? bill.totalMargin.toFixed(2) : "0.00",
    ]);

    doc.autoTable({
      startY: 20,
      head: [
        [
          "S.No",
          "Bill Number",
          "Bill Date",
          "Total Sale Value",
          "Total Cost Value",
          "Gross Margin %",
          "Total Quantity",
          "Total Margin",
        ],
      ],
      body: tableData,
    });

    doc.save("Product_Margin_Report.pdf");
  };

  const filtered = filteredBills
    .map((bill) => {
      // Ensure medicineDetails is an array
      const medicineDetails = Array.isArray(bill.medicineDetails) ? bill.medicineDetails : [];

      // Calculate totalSaleValue as price * quantity for each medicine
      const totalSaleValue = medicineDetails.reduce((sum, med) => {
        const price = Number(med.price) || 0;
        const quantity = Number(med.quantity) || 0;
        if (!Number.isFinite(price) || !Number.isFinite(quantity)) {
          console.warn(`Invalid price or quantity for bill ${bill.billNumber}, med:`, med);
        }
        return sum + price * quantity;
      }, 0);

      const totalQuantity = medicineDetails.reduce((sum, med) => {
        const quantity = Number(med.quantity) || 0;
        return sum + quantity;
      }, 0);

      const totalCostValue = medicineDetails.reduce((sum, med) => {
        const unitsOrStrips = Number(med.unitsOrStrips) || 1;
        const pricePerStrip = Number(med.pricePerStrip) || 0;
        const costValue = unitsOrStrips ? pricePerStrip / unitsOrStrips : 0;
        const quantity = Number(med.quantity) || 0;
        return sum + costValue * quantity;
      }, 0);

      const grossMarginPercent = totalSaleValue > 0
        ? ((totalSaleValue - totalCostValue) / totalSaleValue) * 100
        : 0;

      const totalMargin = totalSaleValue - totalCostValue;

      // Log for debugging
      if (!Number.isFinite(totalSaleValue)) {
        console.warn(`Invalid totalSaleValue for bill ${bill.billNumber}:`, {
          totalSaleValue,
          medicineDetails,
        });
      }

      return {
        ...bill,
        totalSaleValue: Number.isFinite(totalSaleValue) ? totalSaleValue : 0,
        totalCostValue: Number.isFinite(totalCostValue) ? totalCostValue : 0,
        grossMarginPercent: Number.isFinite(grossMarginPercent) ? grossMarginPercent : 0,
        totalQuantity: Number.isFinite(totalQuantity) ? totalQuantity : 0,
        totalMargin: Number.isFinite(totalMargin) ? totalMargin : 0,
      };
    })
    .filter((bill) => {
      const billDate = new Date(bill.billDate).getTime();
      const matchesSearch = bill.billNumber
        ? bill.billNumber.toLowerCase().includes(searchText.toLowerCase())
        : false;
      const matchesDate = billDate >= fromDate && billDate <= toDate;
      return matchesSearch && matchesDate;
    });

  const paginatedData = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <Box p={2}>
      <Loader open={billLoading} />
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={format(new Date(fromDate), "yyyy-MM-dd")}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: format(new Date(), "yyyy-MM-dd"),
              }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={format(new Date(toDate), "yyyy-MM-dd")}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: format(new Date(), "yyyy-MM-dd"),
              }}
            />
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search bill number..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleDownloadPdf}
              disabled={filtered.length === 0}
            >
              Download PDF
            </Button>
          </Stack>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Bill Date</TableCell>
                  <TableCell>Total Sale Value</TableCell>
                  <TableCell>Total Cost Value</TableCell>
                  <TableCell>Gross Margin %</TableCell>
                  <TableCell>Total Quantity</TableCell>
                  <TableCell>Total Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((bill, index) => (
                    <TableRow key={bill.billNumber}>
                      <TableCell>
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{bill.billNumber || "N/A"}</TableCell>
                      <TableCell>
                        {format(new Date(bill.billDate), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>
                        {Number.isFinite(bill.totalSaleValue)
                          ? bill.totalSaleValue.toFixed(2)
                          : "0.00"}
                      </TableCell>
                      <TableCell>
                        {Number.isFinite(bill.totalCostValue)
                          ? bill.totalCostValue.toFixed(2)
                          : "0.00"}
                      </TableCell>
                      <TableCell>
                        {Number.isFinite(bill.grossMarginPercent)
                          ? bill.grossMarginPercent.toFixed(2)
                          : "0.00"}%
                      </TableCell>
                      <TableCell>{bill.totalQuantity || 0}</TableCell>
                      <TableCell>
                        {Number.isFinite(bill.totalMargin)
                          ? bill.totalMargin.toFixed(2)
                          : "0.00"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filtered.length / rowsPerPage)}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              color="primary"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}